/**
 * @module profile/components/portfolio/form_get_entries
 */
import React, { PropTypes, Component } from 'react';
import StarRating from 'common/components/star_rating';
import { v4 } from 'node-uuid';
import classNames from 'classnames';
import ModalFormStep from 'common/components/form/modal_form_step';
import ModalFormUpload from 'common/components/form/modal_form_upload';
import GalleryTile from 'common/components/gallery_tile';

/**
 * Portfolio upload form step component which allows selecting file from
 * a list or renders an upload form.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
export default class SelectEntries extends Component {
  constructor(props) {
    super(props);
    this.state = {
      csWork: false,
      revision: null,
      revisionsView: null,
    };

    this.setIsFromCS = this.setIsFromCS.bind(this);
    this.setIsNew = this.setIsNew.bind(this);
    this.toggleRevisionsView = this.toggleRevisionsView.bind(this);
    this.selectEntry = this.selectEntry.bind(this);
  }

  componentDidMount() {
    this.props.fetchData();
  }

  setIsFromCS() {
    this.setState({ csWork: true });
  }

  setIsNew() {
    this.setState({ csWork: false });
  }

  toggleRevisionsView(entryId) {
    if (entryId) {
      this.setState({ revisionsView: entryId });
    } else {
      this.setState({ revisionsView: null });
    }
  }

  selectEntry(revision) {
    if (this.state.csWork) {
      this.setState({ revision });
      this.props.onChangeEntry({ ...revision }, true);
    }
  }

  renderEntry(entry) {
    const { csWork, revision, revisionsView } = this.state
    let selectedClass = '';

    if (csWork && revision) {
      if ((revisionsView && entry.revisionId === revision.revisionId) ||
        (!revisionsView && entry.entryId === revision.entryId)) {
        selectedClass = 'selected';
      }
    }

    return (
      <div className="row entry" key={v4()}>
        <div className="small-6 column">
          <GalleryTile
            images={[entry.thumbnail]}
            label={entry.label}
            onTileClicked={() => {
              if (revisionsView) {
                this.selectEntry(entry);
              } else {
                this.toggleRevisionsView(entry.entryId)
              }
            }}
            additionalClasses={selectedClass}
          >
            { selectedClass ? <span className="fa fa-check-circle"></span> : null }
          </GalleryTile>
        </div>
        <div className="entry-details column">
          { entry.project &&
          <span className="project-title">
            { entry.project.title }
          </span>}

          <div className="project-categories">
            {entry.sub_category &&
            <span className="tag tag-default tag-sm">{ entry.sub_category.name }</span>}

            {entry.industry &&
            <span className="tag tag-default tag-sm">{ entry.industry.name }</span>}
          </div>

          <div className="star-rating lg">
            <StarRating readonly={true} initialRate={entry.score || 0}/>
          </div>
        </div>
      </div>
    );
  }

  getEntryLabel(entry) {
    let label = '';
    if (entry.withdrawn) {
      label = 'withdrawn';
    }
    if (entry.eliminated) {
      label = 'eliminated';
    }

    return label
  }

  renderEntries() {
    const { entries } = this.props;
    const { revisionsView } = this.state;
    const entriesToRender = [];

    if (revisionsView) {
      const entry = entries.find(e => e.id === revisionsView);
      const label = this.getEntryLabel(entry);

      entry.revisions.forEach(revision => {
        entriesToRender.push({
          thumbnail: revision.thumbnails[0],
          label: label,
          revisionId: revision.id,
          entryId: entry.id,
          project: entry.project,
          sub_category: entry.sub_category,
          industry: entry.industry,
          score: revision.score || 0,
        });
      });
    } else {
      entries.forEach(entry => {
        const label = this.getEntryLabel(entry);
        const revision = entry.revisions[0];

        entriesToRender.push({
          thumbnail: revision.thumbnails[0],
          label: label,
          revisionId: revision.id,
          entryId: entry.id,
          project: entry.project,
          sub_category: entry.sub_category,
          industry: entry.industry,
          score: revision.score || 0,
        });
      });
    }

    return (
      <div className="row entries-list">
        <div className="medium-16 column list-header">
          { revisionsView ?
            <span
              className="list-return"
              onClick={() => this.toggleRevisionsView()}
            >
              &laquo; Back
            </span>
            : <h3>Select entry</h3>
          }
        </div>
        <div className="medium-16 column list-content">
          { entriesToRender.map(entry => this.renderEntry(entry))}
        </div>
      </div>
    );
  }

  renderFileUpload() {
    const fileUploadHeader = (
     <div className="medium-16 column list-header">
       <h3>Upload your work</h3>
     </div>
    );

    return (
      <ModalFormUpload
        header={fileUploadHeader}
        onCrop={this.props.onCrop}
      />
    );
  }

  getContent() {
    const { csWork } = this.state;

    if (csWork) {
      return this.renderEntries();
    }
    return this.renderFileUpload();
  }

  getSidebar() {
    const { csWork } = this.state;
    const { entries } = this.props;

    return (
      <div>
        <fieldset>
          {entries.length > 0 &&
          <div className="radio">
            <input
              id="is-cs"
              type="radio"
              className="form-control"
              checked={csWork === true}
              value={this.state.csWork}
              onChange={this.setIsFromCS}
            />
            <label htmlFor="is-cs" className="form-label">Use crowdspring work</label>
          </div>}
          <div className="radio">
            <input
              id="is-new"
              type="radio"
              className="form-control"
              checked={csWork === false}
              value={this.state.csWork}
              onChange={this.setIsNew}
            />
            <label htmlFor="is-new" className="form-label">Upload my own work</label>
          </div>
        </fieldset>
        { !csWork &&
          <div className="upload-tips">
            <h4>Tips</h4>
            <ul>
              <li>Use GIF, JPG or PNG</li>
              <li>Use RGB colorspace</li>
              <li>800 x 800 px minimum</li>
              <li>5mb max file size</li>
            </ul>
          </div>
        }
      </div>
    );
  }

  render() {
    const { csWork } = this.state;
    const contentClasses = classNames({
      'file-upload-form': csWork,
      'entries-list': !csWork,
    });

    return (
      <ModalFormStep
        sidebar={this.getSidebar()}
        content={this.getContent()}
        buttons={this.props.formButtons}
        contentClass={contentClasses}
      />
    );
  }
}

SelectEntries.propTypes = {
  entries: PropTypes.array,
  categories: PropTypes.array,
  fetchData: PropTypes.func,
  formButtons: PropTypes.node,
  onCrop: PropTypes.func,
  onChangeEntry: PropTypes.func,
};




// WEBPACK FOOTER //
// ./profile/components/portfolio/form_get_entries.jsx